import { queryOptions } from '@tanstack/react-query'

import { parseMoneyInput, type Pesewas } from '@/lib/money'

import { apiClient } from './client'
import {
  INSTITUTION_WIDE_BRANCH_ID,
  type ApprovalAmountBasis,
  type ApprovalAuditEntry,
  type ApprovalAuditEntryPayload,
  type ApprovalAuditOutcome,
  type ApprovalAuditPage,
  type ApprovalAuditPagePayload,
  type ApprovalAuditSource,
  type ApprovalStaffMember,
  type ApprovalStaffMemberPayload,
  type ApprovalStaffPagePayload,
  type ApprovalStaffStatus,
  type ApproveRequestBody,
  type MyApprovalRequest,
  type MyApprovalRequestPayload,
  type MyApprovalRequestStatus,
  type PendingApproval,
  type PendingApprovalPayload,
  type RejectRequestBody,
} from './approvals-types'

/**
 * The approvals endpoints.
 *
 * Three of them belong to this area outright — the inbox, approve, reject — and two are read
 * across a boundary for one question each: the audit trail, which is the only history of a
 * request that exists, and the staff directory, which is the only way to learn that a maker has
 * been disabled *before* pressing Approve rather than after.
 *
 * Normalisation happens here and nowhere above it: identifiers to strings, the major-unit decimal
 * amount to integer pesewas, and the two integer enumerations on an audit row to their names.
 */

/**
 * The server's silent cap on the inbox.
 *
 * `ApprovalService.GetPendingForUserAsync` takes the 500 oldest pending requests and *then*
 * filters them in memory by the permissions the checker holds. Truncation is therefore invisible
 * in the response — the array is at most 500 and reaches 500 only if the checker may approve every
 * one of the 500 oldest — so no client-side banner can detect it. The number is written down here
 * because the inbox says it in words instead.
 */
export const INBOX_CANDIDATE_CAP = 500

/** The entity type the approval engine writes its trail under. */
export const APPROVAL_ENTITY_TYPE = 'ApprovalRequest'

/**
 * Query keys for the area.
 *
 * The trail and the maker lookup hang off the same root as the inbox on purpose: deciding a
 * request changes the queue *and* writes a new row into that request's history, so one
 * invalidation after a decision is both correct and the whole of it.
 */
export const approvalKeys = {
  all: ['approvals'] as const,
  inbox: () => [...approvalKeys.all, 'inbox'] as const,
  mine: () => [...approvalKeys.all, 'mine'] as const,
  trails: () => [...approvalKeys.all, 'trail'] as const,
  trail: (requestId: string, page: number, pageSize: number) =>
    [...approvalKeys.trails(), requestId, page, pageSize] as const,
  maker: (username: string) => [...approvalKeys.all, 'maker', username] as const,
}

const MAJOR_UNIT_TEXT = /^-?\d+(?:\.\d+)?$/
const EXCESS_PRECISION = /\.\d{3,}$/

/**
 * Renders a major-unit amount as text a money parser can read exactly, or null if it is not a
 * number at all.
 *
 * Going through text rather than multiplying is the point. The wire type is `format: double`, so
 * a disbursement of 4500.15 arrives as 4500.149999999999 and `× 100` yields 450014.99999999994 —
 * not the integer count of minor units every amount in this client is required to be. `toFixed(2)`
 * rounds once, deliberately, at the one place the conversion happens.
 */
function majorUnitText(raw: number | string): string | null {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw.toFixed(2) : null
  }

  const trimmed = raw.trim()
  if (!MAJOR_UNIT_TEXT.test(trimmed)) {
    return null
  }

  // A decimal string is already exact; it is only re-rounded when the server sent more precision
  // than a minor unit can hold, which a money parser would otherwise refuse outright.
  if (!EXCESS_PRECISION.test(trimmed)) {
    return trimmed
  }

  const asNumber = Number(trimmed)
  return Number.isFinite(asNumber) ? asNumber.toFixed(2) : null
}

interface NormalisedAmount {
  minor: Pesewas | null
  basis: ApprovalAmountBasis
}

/** Cedis, as a decimal, to an integer count of pesewas — see {@link majorUnitText}. */
export function normaliseApprovalAmount(raw: number | string | null | undefined): NormalisedAmount {
  if (raw === null || raw === undefined) {
    return { minor: null, basis: 'none' }
  }

  const text = majorUnitText(raw)
  if (text === null) {
    return { minor: null, basis: 'unreadable' }
  }

  const parsed = parseMoneyInput(text)
  return parsed.ok
    ? { minor: parsed.value, basis: 'converted-from-major' }
    : { minor: null, basis: 'unreadable' }
}

const AUDIT_SOURCES: Record<number, ApprovalAuditSource> = {
  1: 'Web',
  2: 'Mobile',
  3: 'Api',
  4: 'System',
}

const AUDIT_OUTCOMES: Record<number, ApprovalAuditOutcome> = {
  1: 'Succeeded',
  2: 'Failed',
  3: 'Denied',
}

const STAFF_STATUSES: Record<number, ApprovalStaffStatus> = {
  1: 'Active',
  2: 'Locked',
  3: 'Disabled',
}

/** Identifiers are strings throughout this application; several of these arrive as numbers. */
const asId = (value: number | string) => String(value)
const asOptionalId = (value: number | string | null) => (value === null ? null : String(value))

/** A count sent as `int32` may still arrive quoted, which is what the spec's string union says. */
const asCount = (value: number | string) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function toPendingApproval(payload: PendingApprovalPayload): PendingApproval {
  const branchId = asId(payload.branchId)
  const amount = normaliseApprovalAmount(payload.amount)

  return {
    requestId: payload.requestId,
    requestType: payload.requestType,
    description: payload.description,
    makerUserId: asId(payload.makerUserId),
    makerUsername: payload.makerUsername,
    madeAtUtc: payload.madeAtUtc,
    expiresAtUtc: payload.expiresAtUtc,
    branchId,
    isInstitutionWide: branchId === INSTITUTION_WIDE_BRANCH_ID,
    amountMinor: amount.minor,
    amountBasis: amount.basis,
    amountRaw: payload.amount,
    currencyCode: payload.currencyCode,
  }
}

/**
 * Everything this user may decide on right now.
 *
 * The endpoint takes no parameters and returns no page: the filtering that matters — pending,
 * unexpired, not raised by this user, in a branch they can act for, of a type they hold the
 * countersigning permission for — is all applied on the server. What the screen adds on top is
 * presentation only, over the array it was given.
 *
 * `staleTime: 0` and a minute's polling, because this is a queue two people work at once. A
 * request that was countersigned by a colleague thirty seconds ago must stop looking actionable.
 */
export const approvalInboxQueryOptions = () =>
  queryOptions({
    queryKey: approvalKeys.inbox(),
    queryFn: async () => {
      const payload = (await apiClient.get<PendingApprovalPayload[]>('/access/approvals/inbox')).data

      // Narrowed rather than trusted: an empty inbox and a shape this client does not know are
      // different facts, and only one of them should render as "nothing waiting".
      if (!Array.isArray(payload)) {
        return [] as PendingApproval[]
      }

      return payload.map(toPendingApproval)
    },
    staleTime: 0,
    refetchInterval: 60_000,
  })

const MY_REQUEST_STATUSES: Record<number, MyApprovalRequestStatus> = {
  1: 'Pending',
  2: 'Approved',
  3: 'Rejected',
  4: 'Expired',
  5: 'Executed',
  6: 'Failed',
}

function toMyApprovalRequest(payload: MyApprovalRequestPayload): MyApprovalRequest {
  const branchId = asId(payload.branchId)
  const amount = normaliseApprovalAmount(payload.amount)

  return {
    requestId: payload.requestId,
    requestType: payload.requestType,
    description: payload.description,
    madeAtUtc: payload.madeAtUtc,
    status: MY_REQUEST_STATUSES[payload.status] ?? 'Unknown',
    expiresAtUtc: payload.expiresAtUtc,
    branchId,
    isInstitutionWide: branchId === INSTITUTION_WIDE_BRANCH_ID,
    amountMinor: amount.minor,
    amountBasis: amount.basis,
    amountRaw: payload.amount,
    currencyCode: payload.currencyCode,
    checkerUserId: asOptionalId(payload.checkerUserId),
    checkerUsername: payload.checkerUsername,
    checkedAtUtc: payload.checkedAtUtc,
    checkerNote: payload.checkerNote,
    executedAtUtc: payload.executedAtUtc,
    failureCode: payload.failureCode,
  }
}

/**
 * The maker's own side of the queue: what the caller submitted, pending or decided in roughly the
 * last month. `GET /access/approvals/inbox` never contains these rows — a maker can never be the
 * checker of their own request — so this is the only place "sent for approval" leads to afterwards.
 *
 * Polled at the same rate as the inbox and for the same reason: a request a colleague just decided
 * should stop reading "pending" without the maker having to refresh the page by hand.
 */
export const myRequestsQueryOptions = () =>
  queryOptions({
    queryKey: approvalKeys.mine(),
    queryFn: async () => {
      const payload = (await apiClient.get<MyApprovalRequestPayload[]>('/access/approvals/mine')).data

      if (!Array.isArray(payload)) {
        return [] as MyApprovalRequest[]
      }

      return payload.map(toMyApprovalRequest)
    },
    staleTime: 0,
    refetchInterval: 60_000,
  })

/**
 * Countersigns a request, which runs the maker's stored command through the whole pipeline again —
 * validation, authorisation, transaction — against current balances and current periods.
 *
 * A 204 means the stored command executed. A failure may mean the countersignature was refused
 * (`access.self_approval_forbidden`, `access.approval_permission_missing`,
 * `access.approval_branch_mismatch`, `access.approval_limit_exceeded`,
 * `access.maker_no_longer_active`) or that the command itself failed re-validation — in which case
 * the request deliberately stays pending, because the balance or period that blocked it may be
 * fixable.
 */
export async function approveRequest(requestId: string, body: ApproveRequestBody): Promise<void> {
  await apiClient.post(`/access/approvals/${encodeURIComponent(requestId)}/approve`, body)
}

/** Declines a request. The reason reaches the audit trail and is the only thing the maker is told. */
export async function rejectRequest(requestId: string, body: RejectRequestBody): Promise<void> {
  await apiClient.post(`/access/approvals/${encodeURIComponent(requestId)}/reject`, body)
}

function toAuditEntry(payload: ApprovalAuditEntryPayload): ApprovalAuditEntry {
  return {
    id: asId(payload.id),
    occurredAtUtc: payload.occurredAtUtc,
    actionCode: payload.actionCode,
    entityType: payload.entityType,
    entityId: payload.entityId,
    actorUserId: asOptionalId(payload.actorUserId),
    actorUsername: payload.actorUsername,
    approverUserId: asOptionalId(payload.approverUserId),
    approverUsername: payload.approverUsername,
    branchId: asOptionalId(payload.branchId),
    // An unrecognised value is named as unknown rather than mapped to a plausible one. A trail is
    // evidence, and a guess in it is worse than a gap.
    source: AUDIT_SOURCES[payload.source] ?? 'Unknown',
    outcome: AUDIT_OUTCOMES[payload.outcome] ?? 'Unknown',
    failureReason: payload.failureReason,
    correlationId: payload.correlationId,
    beforeJson: payload.beforeJson,
    afterJson: payload.afterJson,
  }
}

export interface ApprovalTrailQuery {
  /** The approval request's external id, as the engine stamped it on the trail. */
  requestId: string
  page?: number
  pageSize?: number
}

/**
 * The history of one approval request.
 *
 * This is the only history the API exposes: the trail is addressed by entity, never searched, so
 * "every request this month" is a question that has no endpoint. Gated on `ops.audit.view`, and
 * reading it is itself an audited action — which is why it is only ever fired when a panel that
 * shows it is actually open, never speculatively.
 *
 * It is also branch-filtered per reader. The maker's `submitted_for_approval` row carries the
 * maker's branch and the checker's `approved`/`rejected` rows carry the checker's, so a reader
 * without cross-branch reach can be served a genuinely partial trail with `totalCount` already
 * reduced and nothing in the body saying so. The panel that renders this says as much.
 */
export const approvalTrailQueryOptions = ({ requestId, page = 1, pageSize = 25 }: ApprovalTrailQuery) =>
  queryOptions({
    queryKey: approvalKeys.trail(requestId, page, pageSize),
    queryFn: async () => {
      const payload = (
        await apiClient.get<ApprovalAuditPagePayload>(
          `/access/audit/${encodeURIComponent(APPROVAL_ENTITY_TYPE)}/${encodeURIComponent(requestId)}`,
          { params: { page, pageSize } },
        )
      ).data

      // Narrowed before anything is read off it. A page whose shape this client does not know is
      // an empty page, not a thrown TypeError behind the panel — and the count falls back to the
      // page's own length rather than to a zero that would read as "nothing is recorded here",
      // which is the one wrong answer an audit surface must never give.
      const items = Array.isArray(payload?.items) ? payload.items.map(toAuditEntry) : []

      return {
        items,
        page: asCount(payload?.page ?? page) || page,
        pageSize: asCount(payload?.pageSize ?? pageSize) || pageSize,
        totalCount: asCount(payload?.totalCount ?? items.length),
      } satisfies ApprovalAuditPage
    },
    staleTime: 0,
  })

function toStaffMember(payload: ApprovalStaffMemberPayload): ApprovalStaffMember {
  return {
    userId: payload.userId,
    username: payload.username,
    fullName: payload.fullName,
    employeeCode: payload.employeeCode,
    branchId: asId(payload.branchId),
    status: STAFF_STATUSES[payload.status] ?? 'Unknown',
    lockedOut: payload.lockedOut,
    lastLoginUtc: payload.lastLoginUtc,
    roles: Array.isArray(payload.roles) ? payload.roles : [],
  }
}

/**
 * The maker's staff record, looked up by username.
 *
 * One question is being asked: is this account still active. The engine refuses an approval whose
 * maker has since been disabled or deleted — `access.maker_no_longer_active` — and without this
 * the only way to discover that is to press Approve and be refused, having already told the
 * customer it was going through.
 *
 * Behind `admin.user.view`, so it is enrichment and never a precondition: a checker who cannot
 * read the directory sees the request without the maker's status, not an error.
 *
 * The endpoint has no exact-username filter, only free text over username and name, so the match
 * is made here. `null` means the directory answered and this username was not in it — at this
 * branch reach — which is itself worth showing.
 */
export const approvalMakerQueryOptions = (username: string, enabled: boolean) =>
  queryOptions({
    queryKey: approvalKeys.maker(username),
    queryFn: async () => {
      const payload = (
        await apiClient.get<ApprovalStaffPagePayload>('/access/users', {
          params: { search: username, page: 1, pageSize: 25 },
        })
      ).data

      const items = Array.isArray(payload?.items) ? payload.items.map(toStaffMember) : []

      const wanted = username.trim().toLowerCase()
      return items.find((staff) => staff.username.toLowerCase() === wanted) ?? null
    },
    enabled: enabled && username.trim().length > 0,
    staleTime: 5 * 60 * 1000,
  })
